import { formatDate } from '../../utils/date';

/** Friendly labels for well-known internal folder tags */
const KNOWN_FOLDER_LABELS: Record<string, string> = {
  'Initial Records': 'Initial Records',
  'Delivery Records': 'Delivered Files',
  'MRR Delivery Records': 'MRR Review Complete',
};

export function getFolderDisplayName(
  tag: string,
  tagDisplayNames?: Record<string, string>
): string {
  if (tagDisplayNames?.[tag]?.trim()) {
    return tagDisplayNames[tag].trim();
  }
  if (KNOWN_FOLDER_LABELS[tag]) {
    return KNOWN_FOLDER_LABELS[tag];
  }

  const additionalMatch = tag.match(/^Additional ([A-Za-z]+ \d{4})(?:\s*-\s*(\d+))?$/);
  if (additionalMatch) {
    const [, period, idx] = additionalMatch;
    if (idx) return `Additional Records — ${period} (${parseInt(idx, 10) + 1})`;
    return `Additional Records — ${period}`;
  }

  if (tag.startsWith('Additional MRR Delivery Records')) {
    return tag.replace('Additional MRR Delivery Records', 'Additional MRR Delivery');
  }
  if (tag.startsWith('MRR Delivery Records')) {
    return 'MRR Review Complete';
  }

  return tag;
}

export function computeNextAdditionalTagLabel(groupNames: string[]): string {
  const monthYear = formatDate(new Date(), { month: 'short', year: 'numeric' });
  const base = `Additional ${monthYear}`;
  const existing = groupNames.filter((t) => t.startsWith(base));
  if (existing.length === 0) return base;

  let maxCounter = 0;
  for (const tag of existing) {
    const m = tag.match(new RegExp(`Additional ${monthYear.replace(/\s+/g, '\\s+')}(?:\\s*-\\s*(\\d+))?$`));
    if (m) {
      const counter = m[1] ? parseInt(m[1], 10) : 0;
      if (!isNaN(counter)) maxCounter = Math.max(maxCounter, counter);
    }
  }

  if (maxCounter === 0 && existing.length > 0) {
    return `${base} - 1`;
  }

  return `${base} - ${maxCounter + 1}`;
}

export function getUploadTargetLabel(opts: {
  allowAdditionalUploads: boolean;
  isDelivered: boolean;
  isClosed: boolean;
  isCompleted: boolean;
  hadDeliveryEarlier: boolean;
  isMrrDelivery: boolean;
  isCaseDelivery: boolean;
  groupNames: string[];
}): string {
  const {
    allowAdditionalUploads,
    isDelivered,
    isClosed,
    isCompleted,
    hadDeliveryEarlier,
    isMrrDelivery,
    isCaseDelivery,
    groupNames,
  } = opts;

  if (allowAdditionalUploads || (!isDelivered && !isClosed && !isCompleted && hadDeliveryEarlier)) {
    return computeNextAdditionalTagLabel(groupNames);
  }
  if (isMrrDelivery) return 'MRR Delivery Records';
  if (isCaseDelivery) return 'Delivery Records';
  return 'Initial Records';
}

export function getFriendlyUploadTargetLabel(opts: Parameters<typeof getUploadTargetLabel>[0]): string {
  const raw = getUploadTargetLabel(opts);
  return getFolderDisplayName(raw);
}

/** "Additional Oct 2026" or "Additional Oct 2026 - 1". Not a delivery folder. */
export function isAdditionalRecordsTag(tag: string): boolean {
  return /^Additional [A-Za-z]+ \d{4}(?:\s*-\s*\d+)?$/.test(tag);
}

export function latestAdditionalRecordsTag(tags: string[]): string | null {
  const matches = tags.filter(isAdditionalRecordsTag);
  return matches.length > 0 ? matches[matches.length - 1] : null;
}

export function isPriorCaseDeliveryTag(tag: string): boolean {
  return tag === 'Delivered Files'
    || tag === 'Delivery Records'
    || tag.startsWith('Additional Delivery ');
}

function isPriorMrrDeliveryTag(tag: string): boolean {
  return tag === 'MRR Review Complete'
    || tag === 'MRR Delivery Records'
    || tag.startsWith('Additional MRR Delivery');
}

function uniqueFolderName(existingNames: string[], base: string): string {
  if (!existingNames.includes(base)) return base;
  let counter = 1;
  while (existingNames.includes(`${base} - ${counter}`)) counter += 1;
  return `${base} - ${counter}`;
}

export function getMrrDeliveryFolderSuggestions(hasAdditionalCycle: boolean, existingNames: string[] = []): string[] {
  const monthYear = formatDate(new Date(), { month: 'short', year: 'numeric' });
  const defaults = ['MRR Review Complete', 'MRR Delivery Records'];
  const needsNewFolder = hasAdditionalCycle || existingNames.some(isPriorMrrDeliveryTag);
  if (!needsNewFolder) return defaults;
  const next = uniqueFolderName(existingNames, `Additional MRR Delivery ${monthYear}`);
  return [next, ...defaults];
}

export function getCaseDeliveryFolderSuggestions(hasAdditionalCycle: boolean, existingNames: string[] = []): string[] {
  const monthYear = formatDate(new Date(), { month: 'short', year: 'numeric' });
  const defaults = ['Delivered Files', 'Delivery Records'];
  const needsNewFolder = hasAdditionalCycle || existingNames.some(isPriorCaseDeliveryTag);
  if (!needsNewFolder) return defaults;
  const next = uniqueFolderName(existingNames, `Additional Delivery ${monthYear}`);
  return [next, ...defaults];
}
